import { Eye } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { useResumeBuilderActions } from "@/app/context/ResumeBuilderActions/ResumeBuilderActionsContext";
import type { ResumeContextValue } from "@/app/types/resume";

import ErrorToast from "../../../../components/ErrorToast";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips, { type TipsContent } from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import useExtrasnav from "../../../../hooks/use-Extrasnav";
import { CustomExtra } from "../../../../models/resume/customExtra";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";

interface PatchError {
	response: { data: { message: string | string[] } };
}

const customExtraTips: TipsContent = {
	content: [
		{
			bp: false,
			value: "Use this section for anything that does not fit the standard sections, for example hobbies or awards.",
		},
		{ bp: true, value: "Keep it short: three to five bullet points work best." },
		{ bp: true, value: "Only include items that support the job you apply for." },
		{ bp: true, value: "Use the preview to check how the section looks on your resume." },
	],
};

const CustomExtras = () => {
	const [cookies] = useCookies(["resume"]);
	const params = useParams<{ customExtra: string }>();
	const navigate = useNavigate();
	const navigateTo = useExtrasnav();
	const { openPreview } = useResumeBuilderActions();

	const staticValue = new StaticValue();
	const patchSection = usePatchResumeSection();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	//Declaring the additional information state
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [customExtras, setCustomExtras] = useState("");

	useEffect(() => {
		const { data } = resumeData.extras.customExtras;

		setCustomExtras(data);
	}, [resumeData]);

	//submition handler
	const customExtrasInfo = () => {
		const payload = new CustomExtra(customExtras);
		const request = payload.getCustomRequest(params.customExtra);

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.CUSTOM, data: request } as never, {
			onSuccess: successPatchCustom,
			onError: errorPatchCustom,
		});
	};

	function successPatchCustom(ledger: Record<string, unknown>) {
		resumeObjectToContextFormat(ledger, setResumeData);

		navigate(navigateTo);
	}

	function errorPatchCustom(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	return (
		<div>
			<ul className="mb-3 flex items-center justify-end gap-4">
				<li>
					<button
						type="button"
						onClick={openPreview}
						className="flex cursor-pointer items-center gap-2 font-medium text-primary-700"
					>
						<Eye className="size-4" /> Preview
					</button>
				</li>
				<li className="flex items-center font-medium text-primary-700">
					<Tips content={customExtraTips} />
				</li>
			</ul>
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="mb-4 text-gray-900">
				<h2 className="mb-3 font-bold text-2xl">{params.customExtra} Details </h2>
				<p className="mb-0">Add anything else you about {params.customExtra}.</p>
			</div>
			<div className="w-full md:max-w-2xl">
				<TextEditor value={customExtras} setValue={setCustomExtras} placeholder="Add your details here..." />
			</div>
			<div className="mt-10 flex items-center justify-between">
				<Button
					type="button"
					variant="secondary"
					className="rounded-full uppercase"
					size="lg"
					onClick={() => navigate(-1)}
				>
					Back
				</Button>
				<Button type="button" onClick={customExtrasInfo} className="uppercase">
					{patchSection.isPending ? (
						<>
							Loading <Spinner />
						</>
					) : (
						"Next"
					)}
				</Button>
			</div>
		</div>
	);
};

export default CustomExtras;
